import { useMemo } from "react"

import { getCohortTiming } from "components/mentorship/CohortExperience"
import { Button } from "components/ui/button"
import { cn } from "lib/utils"

type MilestoneStatus = "done" | "current" | "upcoming"

export type Milestone = {
  id: string
  name: string
  date: Date | string
  side?: "top" | "bottom"
  status?: MilestoneStatus
}

type MilestonesTimelineProps = {
  startDate: Date | null
  endDate: Date | null
  milestones: Milestone[]
  showMonthTicks?: boolean
  className?: string
  admin?: boolean
  onSetDates?: () => void
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const formatMonthLabel = (date: Date) => date.toLocaleDateString("en-US", { month: "short" })

const formatDateLabel = (date: Date) =>
  date.toLocaleDateString("en-US", { month: "short", day: "numeric" })

const getNextMonthBoundary = (date: Date) => {
  const boundary = new Date(date.getFullYear(), date.getMonth(), 1)
  if (boundary.getTime() <= date.getTime()) boundary.setMonth(boundary.getMonth() + 1)
  return boundary
}

export function MilestonesTimeline({
  startDate,
  endDate,
  milestones,
  showMonthTicks = true,
  className,
  admin = false,
  onSetDates,
}: MilestonesTimelineProps) {
  const timing = getCohortTiming(startDate, endDate)
  const timeline = useMemo(() => {
    if (!startDate || !endDate || endDate.getTime() <= startDate.getTime()) return null
    const totalDuration = endDate.getTime() - startDate.getTime()
    const months: { label: string; position: number }[] = []
    if (showMonthTicks) {
      const cursor = getNextMonthBoundary(startDate)
      while (cursor <= endDate) {
        months.push({
          label: formatMonthLabel(cursor),
          position: ((cursor.getTime() - startDate.getTime()) / totalDuration) * 100,
        })
        cursor.setMonth(cursor.getMonth() + 1)
      }
    }

    const positioned = milestones
      .map((milestone) => {
        const date = milestone.date instanceof Date ? milestone.date : new Date(milestone.date)
        if (Number.isNaN(date.getTime())) return null
        const position = ((date.getTime() - startDate.getTime()) / totalDuration) * 100
        return { ...milestone, position: clamp(position, 0, 100), date, dateLabel: formatDateLabel(date) }
      })
      .filter((milestone): milestone is NonNullable<typeof milestone> => Boolean(milestone))
      .sort((first, second) => first.date.getTime() - second.date.getTime())

    return { monthTicks: months, milestones: positioned }
  }, [endDate, milestones, showMonthTicks, startDate])

  if (!timeline || !startDate || !endDate) {
    return (
      <section className={cn("rounded-2xl border border-[var(--cohort-rule)] bg-[var(--cohort-rest)] p-6", className)} aria-label="Program progress">
        <p className="text-sm text-[var(--cohort-ink-muted)]">Program progress</p>
        <p className="mt-2.5 text-[17px] leading-6">Start and end dates haven&apos;t been set for this cohort.</p>
        <p className="mt-1.5 text-[15px] leading-6 text-[var(--cohort-ink-muted)]">
          {admin ? "Set the program dates to show the cohort timeline." : "Your administrator adds them; the timeline appears here once they do."}
        </p>
        {admin && onSetDates ? <Button type="button" variant="outline" onClick={onSetDates} className="cohort-secondary-button mt-3">Set program dates</Button> : null}
      </section>
    )
  }

  const nextMilestone = timeline.milestones.find((milestone) => milestone.date.getTime() >= Date.now()) ?? null
  const progress = timing.progress ?? 0
  const activeProgress = timing.state === "active"

  return (
    <section className={cn("min-w-[35rem] sm:min-w-0", className)} aria-label="Program progress">
      <div className="mb-5 flex items-center justify-between gap-4 text-sm text-[var(--cohort-ink-muted)]">
        <span>Program progress · {timing.label.toLowerCase()}</span>
        {nextMilestone ? (
          <span>Next milestone: <strong className="font-medium text-[var(--cohort-orange-ink)]">{nextMilestone.name}, {nextMilestone.dateLabel}</strong></span>
        ) : timing.state === "finished" && timing.detail ? <span>{timing.detail}</span> : null}
      </div>

      <div className="relative h-[3px] bg-[var(--cohort-rule)]">
        <div
          className={cn("absolute inset-y-0 left-0", timing.state === "finished" ? "bg-[var(--cohort-radio-idle)]" : "bg-[var(--cohort-forest)]")}
          style={{ width: `${progress}%` }}
        />
        {timeline.milestones.map((milestone) => {
          const reached = milestone.position <= progress
          return (
            <span
              key={milestone.id}
              className={cn("absolute -top-[3px] h-[9px] w-[9px] -translate-x-1/2 rounded-full", reached ? timing.state === "finished" ? "bg-[var(--cohort-radio-idle)]" : "bg-[var(--cohort-forest)]" : "bg-[var(--cohort-toggle-off)]")}
              style={{ left: `${milestone.position}%` }}
              aria-hidden="true"
            />
          )
        })}
      </div>

      <div className="relative mt-2 min-h-6 text-[13px] leading-6">
        {activeProgress ? (
          <span
            className="absolute top-0 whitespace-nowrap font-medium text-[var(--cohort-ink)]"
            style={{ left: `${clamp(progress, 8, 92)}%`, transform: "translateX(-50%)" }}
          >
            Today · {formatDateLabel(new Date())}
          </span>
        ) : null}
      </div>

      {timeline.milestones.length ? (
        <div className="relative min-h-11 text-[13px] leading-[1.45] text-[var(--cohort-ink-muted)]">
          {timeline.milestones.map((milestone) => {
            const isNext = milestone.id === nextMilestone?.id
            const edgeAlignment = milestone.position <= 8
              ? { transform: "translateX(0)", textAlign: "left" as const }
              : milestone.position >= 92
                ? { transform: "translateX(-100%)", textAlign: "right" as const }
                : { transform: "translateX(-50%)", textAlign: "center" as const }
            return (
              <span
                key={milestone.id}
                className={cn("absolute top-0 flex max-w-40 flex-col whitespace-nowrap", isNext && "font-medium text-[var(--cohort-orange-ink)]")}
                style={{ left: `${milestone.position}%`, ...edgeAlignment }}
              >
                <span>{milestone.name}</span>
                <span className={isNext ? "" : "text-[var(--cohort-ink-subtle)]"}>{milestone.dateLabel}</span>
              </span>
            )
          })}
        </div>
      ) : (
        <div className="flex justify-between text-[13px] text-[var(--cohort-ink-muted)]">
          <span>{formatDateLabel(startDate)}</span>
          <span>{formatDateLabel(endDate)}</span>
        </div>
      )}

      {showMonthTicks ? (
        <div className="relative mt-2 h-6">
          {timeline.monthTicks.map((month, index) => (
            <div key={`${month.label}-${index}`} data-testid={`milestones-timeline-month-tick-${index}`} className="absolute flex -translate-x-1/2 flex-col items-center" style={{ left: `${month.position}%` }}>
              <div className="h-2 w-px bg-gray-400" />
              <span className="mt-0.5 text-[10px] font-medium text-gray-600 sm:text-xs">{month.label}</span>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  )
}
